import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { impactStories } from '../data/impactStories';
import './ImpactStoryDetail.css';
import SEO from './SEO';

const ImpactStoryDetail = () => {
  const { id } = useParams();
  const story = impactStories.find(s => s.id === id);

  if (!story) {
    return (
      <div className="story-detail-page">
        <div className="container">
          <div className="story-not-found">
            <h1>Story Not Found</h1>
            <p>The impact story you're looking for doesn't exist.</p>
            <Link to="/impact-stories" className="back-btn">
              ← Back to Impact Stories
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="story-detail-page">
      <SEO
        title={story.name}
        description={story.shortStory}
        image={story.image}
      />
      {/* Hero Section */}
      <div className="story-hero">
        <div className="container">
          <div className="story-nav">
            <Link to="/" className="back-link">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
              Back to Home
            </Link>
          </div>

          <div className="story-hero-content">
            <div className="story-hero-text">
              <div className="story-category">{story.category}</div>
              <h1 className="story-hero-title">{story.name}</h1>
              <div className="story-hero-meta">
                <span className="story-role">{story.role}</span>
                {story.company && (
                  <>
                    <span className="separator">•</span>
                    <span className="story-company">{story.company}</span>
                  </>
                )}
                <span className="separator">•</span>
                <span className="story-country">{story.country}</span>
              </div>
              <p className="story-hero-subtitle">{story.shortStory}</p>
            </div>
            <div className="story-hero-image">
              <img src={story.image} alt={story.name} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Story Content */}
      <div className="story-content">
        <div className="container">
          <div className="story-main">
            {/* Quote Section */}
            {story.quote && (
              <div className="story-quote-section">
                <blockquote className="main-quote">
                  "{story.quote}"
                  <cite>— {story.name}</cite>
                </blockquote>
              </div>
            )}

            {/* Story Content */}
            <div className="story-text">
              {story.fullStory.split('\n\n').map((paragraph, index) => (
                <p key={index} className="story-paragraph">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Additional Quotes */}
            <div className="additional-quotes">
              {story.fellowshipQuote && (
                <blockquote className="fellowship-quote">
                  "{story.fellowshipQuote}"
                </blockquote>
              )}

              {story.inspirationQuote && (
                <blockquote className="inspiration-quote">
                  "{story.inspirationQuote}"
                </blockquote>
              )}

              {story.dreamQuote && (
                <blockquote className="dream-quote">
                  "{story.dreamQuote}"
                </blockquote>
              )}

              {story.leadershipQuote && (
                <blockquote className="leadership-quote">
                  "{story.leadershipQuote}"
                </blockquote>
              )}

              {story.yocilabQuote && (
                <blockquote className="yocilab-quote">
                  "{story.yocilabQuote}"
                </blockquote>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="story-sidebar">
            {/* Sidebar Gallery */}
            <div className="sidebar-section sidebar-image-block">
              <h3>In Pictures</h3>
              {story.gallery && story.gallery.length > 0 ? (
                <div className="sidebar-gallery">
                  {story.gallery.map((img, idx) => (
                    <div className="sidebar-image" key={idx}>
                      <img src={img} alt={`${story.name} gallery ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sidebar-image">
                  <img
                    src={story.impactImage || story.image}
                    alt={`${story.name} impact visual`}
                  />
                </div>
              )}
            </div>

            {/* Achievements */}
            <div className="sidebar-section achievements">
              <h3>Key Achievements</h3>
              <ul className="achievements-list">
                {story.achievements.map((achievement, index) => (
                  <li key={index} className="achievement-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                    {achievement}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tags */}
            <div className="sidebar-section tags">
              <h3>Focus Areas</h3>
              <div className="tags-list">
                {story.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Stories */}
      <div className="related-stories">
        <div className="container">
          <h2 className="related-title">More Changemaker Stories</h2>
          <div className="related-grid">
            {impactStories
              .filter(s => s.id !== story.id)
              .slice(0, 3)
              .map((relatedStory) => (
                <Link
                  key={relatedStory.id}
                  to={`/impact-story/${relatedStory.id}`}
                  className="related-card"
                >
                  <div className="related-image">
                    <img src={relatedStory.image} alt={relatedStory.name} />
                  </div>
                  <div className="related-content">
                    <div className="related-category">{relatedStory.category}</div>
                    <h3 className="related-name">{relatedStory.name}</h3>
                    <p className="related-role">{relatedStory.role}</p>
                    <p className="related-short">{relatedStory.shortStory}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="story-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Be Part of the Movement</h2>
            <p>Join YOCILAB and become the next African changemaker to make impact.</p>
            <div className="cta-buttons">
              <Link to="/seed4impact" className="cta-btn primary">
                Apply for Funding
              </Link>
              <Link to="/contact" className="cta-btn secondary">
                Get Involved
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactStoryDetail;